/**
 * Manage Manual Assets modal (AST-116)
 *
 * Add, edit, archive and reactivate the manual assets under ONE global
 * subtype, for the active customer. Everything is held as local drafts and
 * committed in one all-or-nothing Save (MOD-29). The editing rules live in
 * utils/manualAssetDrafts so they're tested without rendering.
 *
 * Props:
 *   open, onClose()            -- onClose is only called once any discard
 *                                 confirmation has been accepted
 *   customerId
 *   assetType { id, name, iconName }
 *   subtype   { id, name }
 *   onSaved(subtypeName)       -- parent shows the toast and refreshes counts
 *   highlightId                -- optional: open with this row highlighted and
 *                                 scrolled to, turning on "Show archived" if
 *                                 it's archived (AST-115 LIST-25, from search)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControlLabel, IconButton, Switch,
  TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SpriteIcon from './SpriteIcon';
import {
  fetchManualAssets, fetchManualAssetUsage, saveManualAssets,
  isVersionConflict, manualAssetErrorMessage,
} from '../../services/manualAssetsApi';
import {
  NAME_MAX, DESCRIPTION_MAX, DESCRIPTION_COUNTER_FROM,
  toDrafts, blankDraft, isNew, keyOf, hasChanges, hasUnsavedEdits, orderDrafts,
  tally, validateDrafts, serverErrorsToMap, buildBatch, newlyArchived,
} from '../../utils/manualAssetDrafts';

const CONFLICT_MESSAGE = 'These manual assets were changed by someone else. Reload to see the latest version.';

export default function ManageManualAssetsModal({ open, onClose, customerId, assetType, subtype, onSaved, highlightId = null }) {
  const [drafts, setDrafts] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null); // MOD-34
  const [conflict, setConflict] = useState(false);  // MOD-35
  const [errors, setErrors] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'discard'|'reload'|'archive', ... }
  const nameRefs = useRef({});
  const pendingFocus = useRef(null);

  const load = useCallback(async () => {
    if (!customerId || !subtype?.id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchManualAssets(customerId, subtype.id);
      setDrafts(toDrafts(res.items || []));
      setVersion(res.version || 0);
    } catch (err) {
      setLoadError(manualAssetErrorMessage(err, "Couldn't load manual assets."));
    } finally {
      setLoading(false);
    }
  }, [customerId, subtype?.id]);

  // MOD-10: Show archived starts off every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setShowArchived(false);
    setErrors({});
    setSaveError(null);
    setConflict(false);
    setConfirm(null);
    setDrafts([]);
    load();
  }, [open, load]);

  // MOD-12: focus the Name field of a just-added row once it has rendered.
  useEffect(() => {
    const k = pendingFocus.current;
    if (!k) return;
    const el = nameRefs.current[k];
    if (el) {
      el.focus();
      el.scrollIntoView?.({ block: 'nearest' });
      pendingFocus.current = null;
    }
  });

  // LIST-25: opened from a search result -- once rows load, reveal that row.
  // Archived results need "Show archived" on, or the row isn't rendered.
  const highlighted = useRef(null);
  useEffect(() => {
    if (!open) { highlighted.current = null; return; }
    if (!highlightId || highlighted.current === highlightId || loading) return;
    const d = drafts.find((x) => x.id === highlightId);
    if (!d) return;
    if (d.isArchived) setShowArchived(true);
    highlighted.current = highlightId;
    setTimeout(() => nameRefs.current[highlightId]?.scrollIntoView?.({ block: 'center' }), 0);
  }, [open, highlightId, drafts, loading]);

  const ordered = useMemo(() => orderDrafts(drafts), [drafts]);
  const counts = useMemo(() => tally(drafts), [drafts]);
  const savedArchivedCount = useMemo(() => drafts.filter((d) => !isNew(d) && d.original.isArchived).length, [drafts]);
  const visible = useMemo(
    // MOD-11: archived rows only with the toggle on -- but a row the user
    // archived in THIS session stays visible so they can see what they did.
    () => ordered.filter((d) => showArchived || !d.isArchived || isNew(d) || !d.original.isArchived),
    [ordered, showArchived]
  );
  const changed = hasChanges(drafts);
  const busy = saving || loading;

  const update = (key, patch) => {
    setDrafts((ds) => ds.map((d) => (keyOf(d) === key ? { ...d, ...patch } : d)));
    setSaveError(null);
  };

  const addRow = () => {
    const d = blankDraft();
    pendingFocus.current = d.clientId;
    setDrafts((ds) => [...ds, d]);
  };

  const removeRow = (key) => setDrafts((ds) => ds.filter((d) => keyOf(d) !== key));

  // MOD-27: validate on blur. Re-run over everything, since a rename can
  // clear (or create) a duplicate on another row.
  const blurName = (key) => {
    setDrafts((ds) => {
      const next = ds.map((d) => (keyOf(d) === key ? { ...d, name: d.name.trim() } : d)); // MOD-14 trim on blur
      setErrors(validateDrafts(next));
      return next;
    });
  };

  // MOD-4 / MOD-5
  const requestClose = () => {
    if (saving) return;
    if (hasUnsavedEdits(drafts)) setConfirm({ kind: 'discard' });
    else onClose?.();
  };

  const scrollToFirstError = (errs) => {
    const first = ordered.find((d) => errs[keyOf(d)]);
    if (!first) return;
    if (first.isArchived && !showArchived) setShowArchived(true);
    setTimeout(() => nameRefs.current[keyOf(first)]?.scrollIntoView?.({ block: 'center' }), 0);
  };

  const commit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = buildBatch(drafts, { subtypeId: subtype.id, assetTypeId: assetType.id, version });
      await saveManualAssets(customerId, body);
      onSaved?.(subtype.name); // MOD-33
      onClose?.();
    } catch (err) {
      if (isVersionConflict(err)) {
        setConflict(true);
      } else if (err?.status === 400 && err?.data?.fieldErrors?.length) {
        const map = serverErrorsToMap(err.data.fieldErrors);
        setErrors(map);
        scrollToFirstError(map);
      } else {
        setSaveError(manualAssetErrorMessage(err, "Couldn't save manual assets.")); // MOD-34
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    // Drop blank new rows (MOD-28) before validating.
    const kept = drafts.filter((d) => !(isNew(d) && !d.name.trim() && !d.description.trim() && !d.isArchived));
    if (kept.length !== drafts.length) setDrafts(kept);

    const errs = validateDrafts(kept);
    setErrors(errs);
    if (Object.keys(errs).length) { scrollToFirstError(errs); return; } // MOD-31

    // MOD-37: any row being archived that's in use on an active project
    // warns first. Today usage is always 0 (counts don't reference manual
    // assets yet), so this resolves straight through.
    const archiving = newlyArchived(kept);
    if (archiving.length) {
      try {
        const usages = await Promise.all(archiving.map(async (d) => {
          const u = await fetchManualAssetUsage(customerId, d.id);
          return { name: d.name, n: u.activeProjectCount || 0 };
        }));
        const used = usages.filter((u) => u.n > 0);
        if (used.length) { setConfirm({ kind: 'archive', used }); return; }
      } catch {
        // A failed usage check shouldn't block the save; the server is the
        // source of truth and archived counts are kept regardless (MOD-36).
      }
    }
    await commit();
  };

  const reload = async () => {
    setConfirm(null);
    setConflict(false);
    setErrors({});
    await load();
  };

  const archiveWarning = (used) => used.map((u) => `${u.name} is used on ${u.n} active project${u.n === 1 ? '' : 's'}.`).join(' ');

  return (
    <>
      <Dialog
        open={open}
        // MOD-4: Esc closes (via the same confirm path), backdrop click doesn't.
        onClose={(e, reason) => { if (reason !== 'backdropClick') requestClose(); }}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: 1040, width: 'calc(100% - 32px)', m: 2, maxHeight: 'calc(100% - 32px)' } }} // MOD-6
        aria-labelledby="manage-manual-assets-title"
      >
        <DialogTitle id="manage-manual-assets-title" sx={{ pr: 6 }}>
          Manage Manual Assets
          <IconButton aria-label="Close" onClick={requestClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* MOD-7 / MOD-8: display only */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, pb: 2 }}>
          {assetType?.iconName && <SpriteIcon iconName={assetType.iconName} size={32} />}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>ASSET TYPE</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{assetType?.name}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>SUBTYPE</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{subtype?.name}</Typography>
          </Box>
        </Box>

        {/* Toolbar: MOD-9, MOD-10, MOD-12 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }} data-testid="manual-assets-summary">
            Manual Assets · {counts.active} active{counts.archived > 0 ? ` · ${counts.archived} archived` : ''}
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                disabled={busy || savedArchivedCount === 0}
              />
            )}
            label={`Show archived (${savedArchivedCount})`}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={addRow} disabled={busy} sx={{ textTransform: 'none' }}>
            Add Manual Asset
          </Button>
        </Box>

        {conflict && (
          <Alert
            severity="warning"
            sx={{ mx: 3, mt: 2 }}
            action={<Button color="inherit" size="small" onClick={() => setConfirm({ kind: 'reload' })}>Reload</Button>}
          >
            {CONFLICT_MESSAGE}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }} action={<Button color="inherit" size="small" onClick={save}>Try again</Button>}>
            {saveError}
          </Alert>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
            {loadError}
          </Alert>
        )}

        <DialogContent sx={{ px: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Loading manual assets" /></Box>
          ) : drafts.length === 0 && !loadError ? (
            // MOD-22
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No manual assets yet. Add the specific items your crews count for this subtype.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none' }}>
                Add Manual Asset
              </Button>
            </Box>
          ) : (
            <Box role="table" aria-label={`${subtype?.name} manual assets`}>
              {/* MOD-13 */}
              <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '220px 1fr 96px 44px', gap: 2, py: 1 }}>
                <Typography role="columnheader" variant="caption" sx={{ fontWeight: 700, letterSpacing: 1 }}>NAME *</Typography>
                <Typography role="columnheader" variant="caption" sx={{ fontWeight: 700, letterSpacing: 1 }}>DESCRIPTION</Typography>
                <Typography role="columnheader" variant="caption" sx={{ fontWeight: 700, letterSpacing: 1 }}>ARCHIVED</Typography>
                <span role="columnheader" aria-label="Actions" />
              </Box>
              {visible.map((d, i) => {
                const k = keyOf(d);
                const err = errors[k] || {};
                const last = i === visible.length - 1;
                return (
                  <Box
                    key={k}
                    role="row"
                    data-testid={`manual-asset-row-${k}`}
                    data-new={isNew(d) ? 'true' : undefined}
                    data-highlighted={k === highlightId ? 'true' : undefined}
                    sx={{
                      display: 'grid', gridTemplateColumns: '220px 1fr 96px 44px', gap: 2, alignItems: 'start', py: 1,
                      // MOD-20 new rows highlighted; MOD-31 failing rows highlighted;
                      // LIST-25 the search result this modal was opened for.
                      bgcolor: (err.name || err.description) ? 'rgba(207, 48, 40, 0.10)'
                        : isNew(d) ? 'rgba(236, 104, 37, 0.08)'
                          : k === highlightId ? 'rgba(236, 174, 54, 0.14)' : 'transparent',
                      outline: k === highlightId ? '1px solid rgba(236, 174, 54, 0.6)' : 'none',
                      borderRadius: 1, px: 1,
                    }}
                  >
                    {/* MOD-19: dim the text fields of archived rows, never the toggle */}
                    <Box role="cell" sx={{ opacity: d.isArchived ? 0.5 : 1 }}>
                      <TextField
                        inputRef={(el) => { nameRefs.current[k] = el; }}
                        value={d.name}
                        onChange={(e) => update(k, { name: e.target.value.slice(0, NAME_MAX) })} // MOD-25 stops at limit
                        onBlur={() => blurName(k)}
                        error={!!err.name}
                        helperText={err.name || ' '}
                        size="small"
                        fullWidth
                        disabled={saving}
                        inputProps={{ maxLength: NAME_MAX, 'aria-label': 'Name' }}
                      />
                    </Box>
                    <Box role="cell" sx={{ opacity: d.isArchived ? 0.5 : 1 }}>
                      <TextField
                        value={d.description}
                        onChange={(e) => update(k, { description: e.target.value.slice(0, DESCRIPTION_MAX) })} // MOD-26
                        onBlur={() => setErrors(validateDrafts(drafts))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && last) { e.preventDefault(); addRow(); } }} // MOD-21
                        error={!!err.description}
                        helperText={err.description
                          || (d.description.length >= DESCRIPTION_COUNTER_FROM ? `${d.description.length}/${DESCRIPTION_MAX}` : ' ')}
                        placeholder={isNew(d) ? 'Describe this manual asset (size, brand, material…)' : ''}
                        size="small"
                        fullWidth
                        disabled={saving}
                        inputProps={{ maxLength: DESCRIPTION_MAX, 'aria-label': 'Description' }}
                      />
                    </Box>
                    <Box role="cell" sx={{ pt: 0.5 }}>
                      <Switch
                        checked={d.isArchived}
                        onChange={(e) => update(k, { isArchived: e.target.checked })}
                        disabled={saving}
                        inputProps={{ 'aria-label': `Archived: ${d.name || 'new manual asset'}` }}
                      />
                    </Box>
                    <Box role="cell" sx={{ pt: 0.5 }}>
                      {/* MOD-17 / BR-2: only unsaved rows can be removed */}
                      {isNew(d) && (
                        <IconButton aria-label={`Remove ${d.name || 'new row'}`} onClick={() => removeRow(k)} disabled={saving} size="small">
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={requestClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!changed || busy || conflict} // MOD-30
            startIcon={saving ? <CircularProgress size={16} /> : null} // MOD-32
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmations: discard (MOD-5), reload (MOD-35), archive warning (MOD-37).
          Mounted ONLY while in use. Kept mounted with open={!!confirm}, MUI's
          modal manager left the main dialog aria-hidden after the confirm
          closed -- the whole modal vanished for screen readers. Unmounting
          releases the hide properly. */}
      {confirm && (
      <Dialog open onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm?.kind === 'discard' && (
          <>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Keep editing</Button>
              <Button color="error" variant="contained" onClick={() => { setConfirm(null); onClose?.(); }} sx={{ textTransform: 'none' }}>Discard</Button>
            </DialogActions>
          </>
        )}
        {confirm?.kind === 'reload' && (
          <>
            <DialogTitle>Reload manual assets?</DialogTitle>
            <DialogContent><DialogContentText>Your unsaved changes in this window will be lost.</DialogContentText></DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Keep editing</Button>
              <Button variant="contained" onClick={reload} sx={{ textTransform: 'none' }}>Reload</Button>
            </DialogActions>
          </>
        )}
        {confirm?.kind === 'archive' && (
          <>
            <DialogTitle>Archive in-use manual asset?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {archiveWarning(confirm.used)} Archiving removes it from pick lists, but existing counts stay.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button variant="contained" onClick={() => { setConfirm(null); commit(); }} sx={{ textTransform: 'none' }}>Archive anyway</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      )}
    </>
  );
}
